import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { QueryResult, useQuery } from '@apollo/client';

import { BankAccountStatus, FeatureName } from '__generated__/GQL';

import useCanCompanyUserAccessFeature from '../useCanCompanyUserAccessFeature';
import useCompanyContext from '../useCompanyContext';
import {
  CompanyBankAccountsDocument,
  CompanyBankAccountsQuery,
} from './graphql/companyBankAccounts.gql';
import { OnUpdatedBankAccountDocument } from './graphql/onUpdatedBankAccount.gql';

type CompanyBankAccountsQueryContextValue = Pick<
  QueryResult<CompanyBankAccountsQuery>,
  'data' | 'error' | 'loading'
> & { refetch: () => void };

const CompanyBankAccountsQueryContext = createContext<
  CompanyBankAccountsQueryContextValue | undefined
>(undefined);

export const CompanyBankAccountsQueryProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { companyProfileId } = useCompanyContext();
  const { canAccessFeature } = useCanCompanyUserAccessFeature();
  const canReadBankAccount = canAccessFeature({ name: FeatureName.BankInfo });

  const {
    data,
    error,
    loading,
    refetch: queryResultRefetch,
    subscribeToMore,
  } = useQuery(CompanyBankAccountsDocument, {
    fetchPolicy: 'cache-and-network',
    skip: !canReadBankAccount || !companyProfileId,
    variables: {
      companyProfileId: companyProfileId as string,
    },
  });

  useEffect(() => {
    return subscribeToMore({
      document: OnUpdatedBankAccountDocument,
      updateQuery: (prev, { subscriptionData }) => {
        const { updatedBankAccount } = subscriptionData.data;

        if (
          !canReadBankAccount ||
          !companyProfileId ||
          !prev?.viewer?.company?.bankAccounts
        ) {
          return prev;
        }

        const bankAccountIndex = prev.viewer.company.bankAccounts.findIndex(
          ({ bankAccountId }) =>
            bankAccountId === updatedBankAccount.bankAccountId,
        );

        if (bankAccountIndex === -1) {
          return {
            ...prev,
            viewer: {
              ...prev.viewer,
              company: {
                ...prev.viewer.company,
                bankAccounts: [
                  ...prev.viewer.company.bankAccounts,
                  updatedBankAccount,
                ],
              },
            },
          };
        }

        return {
          ...prev,
          viewer: {
            ...prev.viewer,
            company: {
              ...prev.viewer.company,
              bankAccounts: [
                ...prev.viewer.company.bankAccounts.slice(0, bankAccountIndex),
                updatedBankAccount,
                ...prev.viewer.company.bankAccounts.slice(bankAccountIndex + 1),
              ],
            },
          },
        };
      },
    });
  }, [canReadBankAccount, companyProfileId, subscribeToMore]);

  const value = useMemo(() => {
    const refetch = () => {
      if (!canReadBankAccount || !companyProfileId) {
        return;
      }
      queryResultRefetch();
    };

    return {
      data,
      error,
      loading,
      refetch,
    };
  }, [
    data,
    error,
    loading,
    canReadBankAccount,
    companyProfileId,
    queryResultRefetch,
  ]);

  return (
    <CompanyBankAccountsQueryContext.Provider value={value}>
      {children}
    </CompanyBankAccountsQueryContext.Provider>
  );
};

const useCompanyBankAccountsQueryContext = () => {
  const context = useContext(CompanyBankAccountsQueryContext);

  if (!context) {
    throw new Error(
      'useCompanyBankAccountsQueryContext must be used within a CompanyBankAccountsQueryProvider',
    );
  }

  return context;
};

export const useCompanyBankAccounts = ({
  includeClosedBankAccounts = false,
} = {}) => {
  const { data, error, loading, refetch } =
    useCompanyBankAccountsQueryContext();

  const bankAccounts = data?.viewer?.company?.bankAccounts || [];
  const mainBankAccount = bankAccounts.find(({ type }) => type === 'MAIN');
  const isLoading = loading && bankAccounts.length === 0;

  if (includeClosedBankAccounts) {
    return {
      bankAccounts,
      error,
      loading: isLoading,
      mainBankAccount,
      refetch,
    };
  }
  return {
    bankAccounts: bankAccounts.filter(
      ({ status }) => status !== BankAccountStatus.Closed,
    ),
    error,
    loading: isLoading,
    mainBankAccount,
    refetch,
  };
};
